// Example sentences for the कारक tool. Every word is a FormRef resolved by the grammar engine; no form is typed by hand.
// The Hindi and English renderings are curated and, like everything else, await a teacher's review.

import type { FormRef } from '@/prakrit/lib/grammar/formref'
import type { Case } from '@/prakrit/lib/grammar/types'

import {
    AV,
    N,
    SUBJ,
    V,
    formOf,
    resolveRef
} from '@/prakrit/lib/grammar/formref'

export interface KarakExample {
    id: string
    refs: FormRef[]
    hi: string
    en: string
    /** Offered as an example chip in the parser (the others appear only as practice questions). */
    chip: boolean
    /** Part of the shorter Kids list. */
    kids: boolean
}

export const KARAK_EXAMPLES: KarakExample[] = [
    {
        id: 'puriso-potthaam-padhai',
        refs: [N('पुरिस', 'prathama', 'eka'), N('पोत्थअ', 'dvitiya', 'eka'), V('पढ', 'vartaman', 'anya', 'eka')],
        hi: 'आदमी पुस्तक पढ़ता है।',
        en: 'The man reads a book.',
        chip: true,
        kids: true
    },
    {
        id: 'balaa-uvavane-khelanti',
        refs: [N('बालअ', 'prathama', 'bahu'), N('उववण', 'saptami', 'eka'), V('खेल', 'vartaman', 'anya', 'bahu')],
        hi: 'बालक उपवन में खेलते हैं।',
        en: 'The boys play in the garden.',
        chip: true,
        kids: true
    },
    {
        id: 'aham-paiyam-padhami',
        refs: [SUBJ('uttam', 'eka'), N('पाइय', 'dvitiya', 'eka'), V('पढ', 'vartaman', 'uttam', 'eka')],
        hi: 'मैं प्राकृत पढ़ता हूँ।',
        en: 'I read Prakrit.',
        chip: true,
        kids: true
    },
    {
        id: 'sa-maaa-pupphani-anei',
        refs: [SUBJ('anya', 'eka', 'stri'), N('माआ', 'chaturthi', 'eka'), N('पुप्फ', 'dvitiya', 'bahu'), V('आणे', 'vartaman', 'anya', 'eka')],
        hi: 'वह माता के लिए फूल लाती है।',
        en: 'She brings flowers for her mother.',
        chip: true,
        kids: false
    },
    {
        id: 'te-saya-hasanti',
        refs: [SUBJ('anya', 'bahu'), AV('सया'), V('हस', 'vartaman', 'anya', 'bahu')],
        hi: 'वे सदा हँसते हैं।',
        en: 'They always laugh.',
        chip: true,
        kids: false
    },
    {
        id: 'balao-duddham-pivai',
        refs: [N('बालअ', 'prathama', 'eka'), N('दुद्ध', 'dvitiya', 'eka'), V('पिव', 'vartaman', 'anya', 'eka')],
        hi: 'बालक दूध पीता है।',
        en: 'The boy drinks milk.',
        chip: false,
        kids: true
    },
    {
        id: 'siso-ayariam-pucchai',
        refs: [N('सीस', 'prathama', 'eka'), N('आयरिअ', 'dvitiya', 'eka'), V('पुच्छ', 'vartaman', 'anya', 'eka')],
        hi: 'शिष्य आचार्य से पूछता है।',
        en: 'The disciple asks the teacher.',
        chip: false,
        kids: true
    },
    {
        id: 'kisano-khette-kammam-karai',
        refs: [N('किसाण', 'prathama', 'eka'), N('खेत्त', 'saptami', 'eka'), N('कम्म', 'dvitiya', 'eka'), V('कर', 'vartaman', 'anya', 'eka')],
        hi: 'किसान खेत में काम करता है।',
        en: 'The farmer works in the field.',
        chip: false,
        kids: false
    }
]

/** The hint in the free-text field: सो गामं गच्छइ (built by the engine). */
export const HINT_REFS: FormRef[] = [SUBJ('anya', 'eka'), N('गाम', 'dvitiya', 'eka'), V('गच्छ', 'vartaman', 'anya', 'eka')]

const cache = new Map<string, string[]>()

export const exampleWords = (example: KarakExample): string[] => {
    const hit = cache.get(example.id)

    if (hit) return hit

    const words = example.refs.map(formOf)

    cache.set(example.id, words)

    return words
}

export const exampleText = (example: KarakExample): string => `${exampleWords(example).join(' ')}।`

/** The same words in any order count as the same sentence (Prakrit word order is free). */
export const sameWords = (a: string[], b: string[]): boolean => a.length === b.length && [...a].sort().join(' ') === [...b].sort().join(' ')

/** The five roles a learner names in the parser and the practice (Hindi + English labels live in karak-parse.ts). */
export type KarakRole = 'verb' | 'subject' | 'object' | 'adverb' | 'other'

export const KARAK_ROLES: KarakRole[] = ['verb', 'subject', 'object', 'adverb', 'other']

/** The role a word plays, read from the FormRef the sentence was written with (used by the server to grade). */
export const roleOfRef = (ref: FormRef): KarakRole => {
    if (ref.kind === 'verb' || ref.kind === 'as') return 'verb'
    if (ref.kind === 'avyaya') return 'adverb'

    if (ref.kind === 'noun' || ref.kind === 'pronoun') {
        if (ref.case === 'prathama') return 'subject'
        if (ref.case === 'dvitiya') return 'object'
    }

    return 'other'
}

/** The case of a noun or pronoun word (null for verbs and अव्यय). */
export const caseOfRef = (ref: FormRef): Case | null => (ref.kind === 'noun' || ref.kind === 'pronoun' ? ref.case : null)

/** Review keys of every word in the sentence: graded practice uses a sentence only when all of them are usable. */
export const exampleKeys = (example: KarakExample): string[] => example.refs
    .map(ref => resolveRef(ref).core[0]?.key)
    .filter((key): key is string => Boolean(key))

export const getKarakExample = (id: string): KarakExample | undefined => KARAK_EXAMPLES.find(e => e.id === id)
